import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { adminMenu, ROLE_LABELS } from "@/lib/perms";
import { PageHead } from "@/pages/admin/AdminLayout";
import { TempPasswordDialog } from "@/components/admin/TempPasswordDialog";
import { Input } from "@/pages/admin/ManagePages";
import { ROLE_OPTIONS, statusLabel } from "@/pages/admin/UsersPage";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const LEAVE = "Hai modifiche non salvate. Uscire senza salvare?";
const COLS = [["N", "Nessun accesso"], ["V", "Sola visualizzazione"], ["M", "Modifica"]];
const MENU_MODULE = { "": "panoramica", territori: "territori", analisi: "analisi", aziende: "aziende", sedi: "sedi", amministratori: "amministratori", questionario: "questionario", campi: "campi", contenuti: "contenuti" };

export const useUnsavedGuard = (dirty) => {
    const ref = useRef(dirty);
    ref.current = dirty;
    useEffect(() => {
        if (!dirty) return undefined;
        const unload = (e) => { e.preventDefault(); e.returnValue = LEAVE; };
        const click = (e) => {
            const a = e.target.closest?.("a[href]");
            if (a && ref.current && !a.target && !window.confirm(LEAVE)) { e.preventDefault(); e.stopPropagation(); }
        };
        window.history.pushState({ eduGuard: true }, "", window.location.href);
        const pop = () => {
            if (!ref.current) return;
            if (window.confirm(LEAVE)) { ref.current = false; window.history.back(); } else window.history.pushState({ eduGuard: true }, "", window.location.href);
        };
        window.addEventListener("beforeunload", unload);
        document.addEventListener("click", click, true);
        window.addEventListener("popstate", pop);
        return () => { window.removeEventListener("beforeunload", unload); document.removeEventListener("click", click, true); window.removeEventListener("popstate", pop); };
    }, [dirty]);
};

const DataCard = ({ u, spins, readOnly, onSaved, onMsg }) => {
    const [v, setV] = useState({ first_name: u.first_name || "", last_name: u.last_name || "", role: u.role, spin_id: u.spin_id || "" });
    const [errors, setErrors] = useState({});
    const save = async (e) => {
        e.preventDefault();
        const body = { first_name: v.first_name, last_name: v.last_name };
        if (u.can_change_role && v.role !== u.role) body.role = v.role;
        if (v.role === "admin_sede") body.spin_id = v.spin_id || spins[0]?.id;
        try { await api.put(`/admin/users/${u.id}`, body); setErrors({}); onMsg({ kind: "success", text: "Dati aggiornati" }); onSaved(); }
        catch (err) { setErrors(fieldErrors(err) || {}); if (!fieldErrors(err)) onMsg({ kind: "error", text: errorMessage(err) }); }
    };
    const toggle = async (active) => {
        try { await api.put(`/admin/users/${u.id}`, { active }); onMsg({ kind: "success", text: active ? "Account riattivato" : "Account disattivato" }); onSaved(); }
        catch (err) { onMsg({ kind: "error", text: errorMessage(err) }); }
    };
    return (
        <form noValidate onSubmit={save} className="edi-card p-5 space-y-4" aria-labelledby="data-title" data-testid="user-data-card">
            <h2 id="data-title" className="text-xl font-bold">Dati e ruolo</h2>
            <div className="grid gap-4 sm:grid-cols-2">
                <Input id="ud-first_name" label="Nome" value={v.first_name} disabled={readOnly} onChange={(e) => setV({ ...v, first_name: e.target.value })} error={errors.first_name} />
                <Input id="ud-last_name" label="Cognome" value={v.last_name} disabled={readOnly} onChange={(e) => setV({ ...v, last_name: e.target.value })} error={errors.last_name} />
                <div className="sm:col-span-2"><p className="edi-label" id="ud-email-label">Email</p><p className="font-semibold [overflow-wrap:anywhere]" aria-labelledby="ud-email-label" data-testid="ud-email">{u.email}</p></div>
                {u.can_change_role ? (
                    <Input as="select" id="ud-role" label="Ruolo" value={v.role} onChange={(e) => setV({ ...v, role: e.target.value })} error={errors.role}>
                        {ROLE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </Input>
                ) : <div><p className="edi-label">Ruolo</p><p className="font-semibold" data-testid="ud-role-text">{ROLE_LABELS[u.role]}</p></div>}
                {v.role === "admin_sede" ? (
                    <Input as="select" id="ud-spin" label="Sede" value={v.spin_id} disabled={readOnly} onChange={(e) => setV({ ...v, spin_id: e.target.value })} error={errors.spin_id}>
                        {spins.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Input>
                ) : <div><p className="edi-label">Sede</p><p className="font-semibold">Tutte le sedi</p></div>}
            </div>
            {u.can_change_role && v.role !== u.role && <p className="text-sm text-edi-muted">Cambiando ruolo i permessi tornano ai predefiniti del nuovo ruolo.</p>}
            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-edi-border pt-4">
                <div className="flex items-center gap-3">
                    <Switch id="ud-active" checked={u.active} disabled={readOnly || u.invitation_pending} onCheckedChange={toggle} data-testid="ud-active-switch" />
                    <label htmlFor="ud-active">Account attivo <span className="text-edi-muted">({statusLabel(u)})</span></label>
                </div>
                {!readOnly && <button type="submit" className="btn-secondary py-2" data-testid="ud-save-data">Salva dati</button>}
            </div>
        </form>
    );
};

const AccountActions = ({ u, onMsg, onSaved, backTo }) => {
    const navigate = useNavigate();
    const run = async (fn, ok) => { try { const { data } = await fn(); onMsg({ kind: "success", text: data.message || ok }); onSaved(); return true; } catch (e) { onMsg({ kind: "error", text: errorMessage(e) }); return false; } };
    return (
        <section className="edi-card p-5 space-y-4" aria-labelledby="actions-title" data-testid="user-actions-card">
            <h2 id="actions-title" className="text-xl font-bold">Azioni sull'account</h2>
            <div className="flex flex-wrap gap-3">
                {u.invitation_pending && <button type="button" className="btn-secondary py-2" onClick={() => run(() => api.post(`/admin/users/${u.id}/resend-invite`), "Invito inviato")} data-testid="ud-resend-invite">Reinvia invito</button>}
                <TempPasswordDialog endpoint={`/admin/users/${u.id}/temp-password`} email={u.email} onDone={(m) => { onMsg(m); onSaved(); }} testPrefix="ud-temp" />
                <AlertDialog>
                    <AlertDialogTrigger asChild><button type="button" className="btn-secondary py-2 text-edi-error" data-testid="ud-delete">Elimina account</button></AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare l'account di {u.email}?</AlertDialogTitle>
                            <AlertDialogDescription>L'operazione è definitiva e non può essere annullata.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="ud-delete-cancel">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={async () => { if (await run(() => api.delete(`/admin/users/${u.id}`), "Account eliminato")) navigate(backTo); }} data-testid="ud-delete-confirm">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </section>
    );
};

const PermissionGrid = ({ matrix, vals, setVals, readOnly }) => (
    <div className="space-y-6">
        <div className="overflow-x-auto" role="region" aria-label="Permessi per sezione" tabIndex={0}>
            <table className="w-full text-left text-sm" data-testid="perm-grid">
                <caption className="sr-only">Livello di accesso per ogni sezione</caption>
                <thead><tr className="border-b-2 border-edi-border-strong">
                    <th scope="col" className="py-2 pr-4">Sezione</th>
                    {COLS.map(([, l]) => <th key={l} scope="col" className="py-2 px-2 text-center w-28">{l}</th>)}
                </tr></thead>
                <tbody>
                    {matrix.filter((r) => r.kind === "module").map((r) => (
                        <tr key={r.key} className="border-b border-edi-border" data-testid={`perm-row-${r.key}`}>
                            <th scope="row" className="py-3 pr-4 font-normal">
                                <span className="font-semibold block">{r.label}</span>
                                <span className="text-edi-muted text-sm">{r.description}</span>
                            </th>
                            {COLS.map(([lv, l]) => (
                                <td key={lv} className="py-3 px-2 text-center">
                                    {r.levels.some((x) => x.value === lv) ? (
                                        <input type="radio" className="h-5 w-5 accent-[var(--edi-text)] cursor-pointer" name={`perm-${r.key}`} value={lv}
                                            checked={vals[r.key] === lv} disabled={readOnly} aria-label={`${r.label}: ${l}`}
                                            onChange={() => setVals({ ...vals, [r.key]: lv })} data-testid={`perm-${r.key}-${lv}`} />
                                    ) : <span aria-label={`${r.label}: ${l} non disponibile`} role="img">—</span>}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <table className="w-full text-left text-sm" data-testid="ops-table">
            <caption className="text-left font-heading font-bold text-base pb-2">Operazioni</caption>
            <thead><tr className="border-b-2 border-edi-border-strong"><th scope="col" className="py-2 pr-4">Operazione</th><th scope="col" className="py-2 w-28 text-center">Consentita</th></tr></thead>
            <tbody>
                {matrix.filter((r) => r.kind === "operation").map((r) => (
                    <tr key={r.key} className="border-b border-edi-border" data-testid={`perm-row-${r.key}`}>
                        <th scope="row" className="py-3 pr-4 font-normal"><span className="font-semibold block">{r.label}</span><span className="text-edi-muted text-sm">{r.description}</span></th>
                        <td className="py-3 text-center">
                            <Switch checked={vals[r.key] === "C"} disabled={readOnly} aria-label={`${r.label}: consentita`}
                                onCheckedChange={(on) => setVals({ ...vals, [r.key]: on ? "C" : "N" })} data-testid={`perm-${r.key}-switch`} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const Preview = ({ role, vals, spinName }) => {
    const menu = adminMenu({ role, permissions: vals, spin: { name: spinName } }).filter(([p]) => p !== "account");
    const badge = (path) => {
        if (path === "esporta") return "Download";
        if (path === "analytics") return "Sola lettura";
        return vals[MENU_MODULE[path]] === "M" ? "Modifica" : "Sola lettura";
    };
    return (
        <aside className="edi-card p-5" aria-labelledby="preview-title" data-testid="user-preview">
            <h2 id="preview-title" className="text-xl font-bold">Cosa vede questo utente</h2>
            <p className="text-sm text-edi-muted mt-1">Anteprima del menu con le scelte attuali, prima del salvataggio.</p>
            <ul className="mt-4 space-y-2" aria-live="polite">
                {menu.length === 0 && <li data-testid="preview-empty">Nessuna sezione disponibile</li>}
                {menu.map(([path, label, Icon]) => (
                    <li key={path} className="flex items-center justify-between gap-3" data-testid={`preview-item-${path || "home"}`}>
                        <span className="inline-flex items-center gap-2">{Icon && <Icon aria-hidden="true" size={16} />}{label}</span>
                        <span className={badge(path) === "Modifica" ? "edi-level-badge" : "rounded-lg border border-edi-border px-3 py-1 text-sm"}>{badge(path)}</span>
                    </li>
                ))}
            </ul>
        </aside>
    );
};

const PRESETS = [
    ["default", "Predefiniti del ruolo", (m) => Object.fromEntries(m.map((r) => [r.key, r.default]))],
    ["readonly", "Solo lettura", (m) => Object.fromEntries(m.map((r) => [r.key, r.kind === "operation" ? "N" : "V"]))],
    ["full", "Accesso completo", (m) => Object.fromEntries(m.map((r) => [r.key, r.levels[r.levels.length - 1].value]))],
];

export const UserDetail = ({ system }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const backTo = system ? "/amministrazione/utenti" : "/amministrazione/amministratori";
    const [u, setU] = useState(null);
    const [spins, setSpins] = useState([]);
    const [vals, setVals] = useState({});
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const load = () => api.get(`/admin/users/${id}`).then(({ data }) => { setU(data); setVals(Object.fromEntries(data.matrix.map((r) => [r.key, r.value]))); })
        .catch((e) => setError(errorMessage(e)));
    useEffect(() => { load(); api.get("/admin/filter-options").then(({ data }) => setSpins(data.spin || [])); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    const saved = useMemo(() => Object.fromEntries((u?.matrix || []).map((r) => [r.key, r.value])), [u]);
    const dirty = !!u && Object.keys(vals).some((k) => vals[k] !== saved[k]);
    useUnsavedGuard(dirty);
    const fullName = u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "";
    usePageTitle(u ? `Utenti e permessi › ${fullName}` : "Utenti e permessi");
    if (error) return <Notice kind="error" role="alert" testId="user-detail-error">{error}</Notice>;
    if (!u) return <Loading />;
    const readOnly = !u.can_edit;
    const back = () => { if (!dirty || window.confirm(LEAVE)) navigate(backTo); };
    const savePerms = async () => {
        setBusy(true);
        try { const { data } = await api.put(`/admin/users/${u.id}/permissions`, { permissions: vals }); setU({ ...u, matrix: data.matrix }); setMsg({ kind: "success", text: data.message }); }
        catch (e) { setMsg({ kind: "error", text: errorMessage(e) }); }
        finally { setBusy(false); }
    };
    return (
        <>
            <PageHead title={`${system ? "Utenti e permessi" : "Amministratori di sede"} › ${fullName}`} subtitle={`${ROLE_LABELS[u.role]} · ${u.role === "admin_sede" ? u.spin_name : "Tutte le sedi"}`}>
                <button type="button" className="inline-flex items-center gap-2 font-semibold underline underline-offset-4 text-edi-on-graphite" onClick={back} data-testid="user-back-link">
                    <ArrowLeft aria-hidden="true" size={18} /> Torna all'elenco
                </button>
            </PageHead>
            {readOnly && <div className="mb-4"><Notice kind="info" testId="user-readonly-note">Sola visualizzazione: non hai il permesso di modificare questo account.</Notice></div>}
            <div className="mb-4" aria-live="polite">{msg && <Notice kind={msg.kind} role={msg.kind === "error" ? "alert" : "status"} testId="user-detail-message">{msg.text}</Notice>}</div>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] items-start">
                <div className="space-y-6 min-w-0">
                    <DataCard key={`${u.role}-${u.active}-${u.spin_id}`} u={u} spins={spins} readOnly={readOnly} onSaved={load} onMsg={setMsg} />
                    {!readOnly && <AccountActions u={u} onMsg={setMsg} onSaved={load} backTo={backTo} />}
                    {u.role === "system_admin" ? (
                        <section className="edi-card p-5" data-testid="user-perms-card"><h2 className="text-xl font-bold">Permessi</h2><p className="mt-2">L'Amministrazione di sistema ha sempre accesso completo a tutte le sezioni.</p></section>
                    ) : (
                        <section className="edi-card p-5 space-y-5" aria-labelledby="perms-title" data-testid="user-perms-card">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <h2 id="perms-title" className="text-xl font-bold">Permessi</h2>
                                {!readOnly && (
                                    <div className="flex flex-wrap gap-2" role="group" aria-label="Preimpostazioni">
                                        {PRESETS.map(([k, l, fn]) => <button key={k} type="button" className="btn-secondary py-1.5 px-3 text-sm" onClick={() => setVals(fn(u.matrix))} data-testid={`preset-${k}`}>{l}</button>)}
                                    </div>
                                )}
                            </div>
                            <PermissionGrid matrix={u.matrix} vals={vals} setVals={setVals} readOnly={readOnly} />
                        </section>
                    )}
                </div>
                {u.role !== "system_admin" && <div className="xl:sticky xl:top-[calc(var(--edi-header-h,80px)+16px)]"><Preview role={u.role} vals={vals} spinName={u.spin_name} /></div>}
            </div>
            {!readOnly && u.role !== "system_admin" && (
                <div className="sticky bottom-0 z-30 mt-6 -mx-1 border-t border-edi-border bg-edi-surface/95 backdrop-blur px-4 py-3 flex flex-wrap items-center justify-between gap-3 rounded-t-xl shadow-[0_-4px_16px_rgba(35,41,44,0.08)]" data-testid="perm-save-bar">
                    <p className="text-sm" role="status" aria-live="polite" data-testid="perm-dirty-status">{dirty ? "Modifiche non salvate" : "Nessuna modifica da salvare"}</p>
                    <div className="flex gap-3">
                        <button type="button" className="btn-secondary py-2" disabled={!dirty || busy} onClick={() => setVals(saved)} data-testid="perm-cancel-button">Annulla</button>
                        <button type="button" className="btn-primary py-2" disabled={!dirty || busy} onClick={savePerms} data-testid="perm-save-button">Salva permessi</button>
                    </div>
                </div>
            )}
        </>
    );
};
